import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  FIRM_STATUSES, NEW_FIRM, TAG_RE, firmStatusText, newFirmPayload, normalizeTag, parseStates,
  suggestedLabel, type FirmValues,
} from "@/lib/firms";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCreateFirm } from "@/lib/queries";
import type { Firm } from "@/lib/types";

/**
 * New firm (ADR-045). The code is the canonical tag — what a lead carries to name the firm — and is
 * immutable, so the dialog says so; the label is what badges and chips print, defaulted from the
 * sheet's convention ("[CA] Larry H. Parker") when left empty. Editing a firm is its Settings tab.
 */

const schema = z.object({
  code: z.string().min(1, "Required").refine((v) => TAG_RE.test(normalizeTag(v)), { message: "Letters, digits and underscores, like lhpark_ca" }),
  name: z.string().trim().min(1, "Required").max(200),
  label: z.string().trim().max(120),
  states: z.string().refine((v) => parseStates(v).problem === null, { message: "Two-letter state codes, comma-separated" }),
  languages: z.array(z.string()).min(1, "At least one language"),
  status: z.string(),
  pause_reason: z.string().max(500),
  system_of_record: z.string(),
});

export function FirmFormDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (firm: Firm) => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const create = useCreateFirm();
  const [formError, setFormError] = React.useState<string | null>(null);
  const form = useForm<FirmValues>({ resolver: zodResolver(schema), defaultValues: NEW_FIRM });
  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = form;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    reset(NEW_FIRM);
    setFormError(null);
  }, [open, reset]);

  const values = watch();
  const placeholderLabel = suggestedLabel(values.name || "Firm", parseStates(values.states).states);

  const submit = handleSubmit((submitted) => {
    setFormError(null);
    create.mutate(
      { ...newFirmPayload(submitted), ...(user?.role === "superadmin" && user.tenant_id ? { tenant_id: user.tenant_id } : {}) },
      {
        onSuccess: (firm) => {
          toast.success("Firm added", firm.label);
          onOpenChange(false);
          onCreated?.(firm);
        },
        onError: (e) => setFormError(e instanceof ApiError ? e.message : "Could not add the firm."),
      },
    );
  });

  const toggleLanguage = (code: string, on: boolean) => {
    const next = new Set(values.languages);
    if (on) next.add(code);
    else next.delete(code);
    setValue("languages", [...next], { shouldDirty: true, shouldValidate: true });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle>New firm</DialogTitle>
          <DialogDescription>
            A client whose leads the floor works. Its leads are worked on the shared calling campaign; a lead names its firm by a tag.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={submit} noValidate>
          <DialogBody className="space-y-4">
            <DiscardGuard changed={changed} />
            <Field label="Name" htmlFor="firm-name" required error={errors.name?.message}>
              <Input {...register("name")} autoComplete="off" placeholder="The Law Offices of Larry H. Parker" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Code"
                htmlFor="firm-code"
                required
                error={errors.code?.message}
                hint="The canonical tag a lead carries. Cannot be changed later."
              >
                <Input {...register("code")} autoComplete="off" className="font-mono" placeholder="lhpark_ca" />
              </Field>
              <Field label="States" htmlFor="firm-states" error={errors.states?.message} hint="Two-letter codes, comma-separated">
                <Input {...register("states")} autoComplete="off" placeholder="CA" className="uppercase" />
              </Field>
            </div>
            <Field label="Label" htmlFor="firm-label" error={errors.label?.message} hint="What badges and chips print. Left empty, the sheet's convention.">
              <Input {...register("label")} autoComplete="off" placeholder={placeholderLabel} />
            </Field>
            <fieldset>
              <legend className="mb-1.5 text-xs font-medium text-text">Languages</legend>
              <div className="flex gap-4 text-sm">
                {(["en", "es"] as const).map((code) => (
                  <label key={code} className="inline-flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      className="accent-accent-9"
                      checked={values.languages.includes(code)}
                      onChange={(e) => toggleLanguage(code, e.target.checked)}
                    />
                    {code === "en" ? "English" : "Spanish"}
                  </label>
                ))}
              </div>
              {errors.languages?.message && <p className="mt-1 text-xs text-danger-11">{errors.languages.message}</p>}
            </fieldset>
            <Field label="Status" htmlFor="firm-status" hint={firmStatusText(values.status).description}>
              <Select value={values.status} onValueChange={(v) => setValue("status", v, { shouldDirty: true })}>
                <SelectTrigger id="firm-status" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIRM_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {firmStatusText(status).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {formError && <Callout tone="danger">{formError}</Callout>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Add firm
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
